import { useTheme } from '@mui/material';
import MuiStep, { type StepProps as MuiStepProps } from '@mui/material/Step';
import MuiStepButton from '@mui/material/StepButton';
import MuiStepContent, {
  type StepContentProps as MuiStepContentProps,
} from '@mui/material/StepContent';
import MuiStepLabel from '@mui/material/StepLabel';
import type { ReactElement } from 'react';

export interface BaseStepProps<StepType extends string> extends MuiStepProps {
  label: StepType;
  error?: boolean;
  disabled?: boolean;
  onLabelClick?(step: StepType): void;
  transitionProps?: MuiStepContentProps['TransitionProps'];
}

export const BaseStep = <StepType extends string>({
  label,
  children,
  error,
  disabled = false,
  onLabelClick,
  transitionProps,
  sx,
  ...props
}: BaseStepProps<StepType>): ReactElement => {
  const theme = useTheme();

  const handleLabelClick = (): void => {
    if (disabled) return;

    onLabelClick?.(label);
  };

  return (
    <MuiStep
      sx={{
        '& .MuiStepLabel-iconContainer': {
          paddingRight: 2,
          '.MuiSvgIcon-root': {
            fill: theme.palette.SlabIndigo[500],
          },
        },
        '& .MuiStepIcon-root.Mui-error': {
          fill: theme.palette.SlabRed[600],
        },
        '& .MuiStepLabel-label.Mui-error': {
          color: theme.palette.SlabRed[600],
        },
        '& .MuiStepLabel-label': {
          color: theme.palette.SlabGray[900],
          fontWeight: 400,
          fontSize: 28,
        },
        '& .Mui-disabled': {
          '.MuiStepLabel-label': {
            color: theme.palette.SlabGray[500],
          },
          '.MuiSvgIcon-root': {
            fill: theme.palette.SlabGray[500],
          },
        },
        ...sx,
      }}
      {...props}
    >
      <MuiStepButton sx={{ m: 0, p: 0 }} disabled={disabled} onClick={handleLabelClick}>
        <MuiStepLabel error={error}>{label}</MuiStepLabel>
      </MuiStepButton>

      <MuiStepContent TransitionProps={transitionProps}>{children}</MuiStepContent>
    </MuiStep>
  );
};
